import { useContext, useEffect } from 'react';
import { CableContext } from '../contexts/cable';

export type UpdateMessage = {
  type: string;
  id?: string;
  [k: string]: unknown;
};

/**
 * Subscribe to backend-pushed update signals on the single UpdatesChannel
 * the CableProvider holds open. The handler receives every message; filter
 * by `msg.type` and `msg.id` inside the handler.
 *
 * Pass `onReconnect` to recover state after a WebSocket reconnect: typically
 * invalidate the page's TanStack queries so any events missed during the
 * disconnect are recovered via refetch.
 *
 * Payloads are signals only ({ type, id, ... }) — never put business data on
 * the wire. To get details, refetch through the normal authenticated APIs.
 */
export const useUpdates = (
  onMessage: (msg: UpdateMessage) => void,
  onReconnect?: () => void,
): void => {
  const { onMessage: register, onReconnect: registerReconnect } = useContext(CableContext);

  useEffect(() => register(onMessage), [register, onMessage]);

  useEffect(() => {
    if (!onReconnect) return undefined;
    return registerReconnect(onReconnect);
  }, [registerReconnect, onReconnect]);
};
